import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, AlertTriangle } from "lucide-react";
import { adminApi, errText, fmtDate } from "@/lib/api";

export function ConversationsTab() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminApi
      .get("/admin/conversations")
      .then(({ data }) => setConvos(data))
      .catch((e) => toast.error(errText(e)));
  }, []);

  return (
    <div className="mt-5 grid lg:grid-cols-[340px_1fr] gap-4">
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="conversations-list">
        {convos.length === 0 && (
          <p className="text-[#71717A] text-sm py-10 text-center">No chatbot conversations yet.</p>
        )}
        {convos.map((c) => {
          const isSel = selected?.session_id === c.session_id;
          return (
            <button
              key={c.session_id}
              data-testid={`conversation-item-${c.session_id}`}
              onClick={() => setSelected(c)}
              className={`w-full text-left bg-white border rounded-sm p-3.5 transition-colors ${
                isSel ? "border-[#FF5A00]" : "border-[#E4E4E7] hover:border-[#D4AF37]"
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#0A0A0A]">
                  {c.lead?.name || c.phone || "Anonymous visitor"}
                </p>
                {c.unanswered_count > 0 && (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-red-600">
                    <AlertTriangle className="w-3 h-3" /> {c.unanswered_count} unanswered
                  </span>
                )}
              </div>
              <p className="text-xs text-[#71717A] mt-1">
                {c.phone ? `📱 ${c.phone} · ` : ""}{(c.messages || []).length} messages · {fmtDate(c.updated_at)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 max-h-[70vh] overflow-y-auto" data-testid="conversation-transcript">
        {!selected ? (
          <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-[#A1A1AA]">
            <MessageSquare className="w-10 h-10 mb-3" />
            <p className="text-sm">Select a conversation to view the transcript</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            <p className="text-xs text-[#71717A] pb-2 border-b border-[#E4E4E7]">
              Session {selected.session_id.slice(0, 8)} · {selected.phone ? `Linked to ${selected.phone}` : "Not linked to a lead"}
            </p>
            {(selected.messages || []).map((m, i) => {
              let bubbleCls;
              if (m.role === "bot") {
                bubbleCls = "bg-[#F4F4F5] text-[#0A0A0A]";
              } else if (m.unanswered) {
                bubbleCls = "bg-red-50 border border-red-200 text-red-800";
              } else {
                bubbleCls = "bg-[#FF5A00] text-white";
              }
              return (
                <div key={i} className={`flex ${m.role === "bot" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-sm px-3 py-2 text-sm ${bubbleCls}`}>
                    {m.unanswered && <p className="text-[10px] font-bold text-red-600 mb-1">⚠ UNANSWERED</p>}
                    {m.text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
